import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './utils/AuthContext';
import { StoreProvider } from './utils/StoreContext';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';
import MovementRecorder from './pages/MovementRecorder';
import ItemList from './pages/ItemList';
import Login from './pages/Login';
import Settings from './pages/Settings';
import './App.css';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('🛡️ ProtectedRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);
  
  // Show loading spinner while checking authentication
  if (isLoading) {
    console.log('🛡️ ProtectedRoute - Showing loading spinner');
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    console.log('🛡️ ProtectedRoute - Not authenticated, redirecting to /login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('🛡️ ProtectedRoute - Authenticated, rendering children');
  return <>{children}</>;
};

// Main App Component
const AppContent: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Debug logging
  console.log('🚀 App render - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);

  return (
    <Router>
      <div className="App">
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              isLoading ? (
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                  </div>
                </div>
              ) : isAuthenticated ? <Navigate to="/" replace /> : <Login />
            } 
          />
          
          {/* Protected Routes */}
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="movement-recorder" element={<MovementRecorder />} />
            <Route path="items" element={<ItemList />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          
          {/* Catch all route - redirect to login if not authenticated */}
          <Route 
            path="*" 
            element={
              isLoading ? (
                <div className="flex items-center justify-center min-h-screen bg-gray-50">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading...</p>
                  </div>
                </div>
              ) : isAuthenticated ? <Navigate to="/" replace /> : <Navigate to="/login" replace />
            } 
          />
        </Routes>
      </div>
    </Router>
  );
};

// Root App Component with Auth and Store Providers
const App: React.FC = () => {
  return (
    <AuthProvider>
      <StoreProvider>
        <AppContent />
      </StoreProvider>
    </AuthProvider>
  );
};

export default App;
